import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import { mdiAirplaneTakeoff } from '@mdi/js';
import ManageBookingsHeader from './ManageBookingsHeader';
import WebActivityIndicatorComponent from './WebActivityIndicatorComponent';
import MessageThread from './MessageThread';
import { bookingApi } from '../api/booking';
import avatarPlaceholder from '../assets/avatar_user_5.png';
import './MessagesView.css';

const PAGE_SIZE = 30;

// Human labels for booking status pills. `pending` is the "requires approval" state —
// there is no separate requires_approval status (statuses: pending/confirmed/cancelled/completed).
const STATUS_LABELS = {
    pending: 'Requires approval',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    completed: 'Completed',
};

/**
 * MessagesView — conversation inbox (Outlook two-pane layout, Messenger ordering).
 *
 * Desktop: two panes — a person-first conversation list (left) and a reading
 * pane (right) hosting the selected booking's MessageThread. Click a row → the
 * right pane populates in place (no modal).
 *
 * Narrow (<= 900px): collapses to list-only; selecting a row shows the
 * MessageThread full-screen with a back button (the mobile pattern).
 *
 * Data source: self-fetches scope=inbox (bookingApi.getInstructorInbox), which
 * returns EVERY conversation-bearing booking (any date/status), server-sorted
 * newest-message-first, a page at a time. This is DECOUPLED from the dashboard's
 * calendar date window — the inbox shows all conversations, not just those on
 * bookings in the currently-viewed month. Older conversations load via the
 * "Load more messages" button (no scroll-sensing). Shares the scope=inbox
 * endpoint with the mobile inbox.
 */
const MessagesView = ({
    currentUserId,
    travelPeriods = [],
    onBackToBookings,
    onRefresh,
    onMessageSent,
    onMessagesRead,
    onViewBookingDetails
}) => {
    const [selectedBookingId, setSelectedBookingId] = useState(null);
    const [conversations, setConversations] = useState(null); // null = not yet loaded
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Identical logic to BookingWeekView.isTravelBooking: a booking is "travel" when its
    // date falls inside a travel period. Returns the matching period (for its colour + location).
    const travelPeriodFor = booking => {
        if (!travelPeriods || travelPeriods.length === 0) return null;
        const bookingDate = new Date(booking.bookingDate);
        return travelPeriods.find(period => {
            const start = new Date(period.startDate);
            const end = new Date(period.endDate);
            return bookingDate >= start && bookingDate <= end;
        }) || null;
    };

    // Load the first page (offset 0). Server sorts newest-message-first, so the
    // client does not re-sort. Replaces the list — used on mount and on refresh.
    const loadInbox = useCallback(async () => {
        try {
            const { bookings: page, hasMore: more } = await bookingApi.getInstructorInbox({
                limit: PAGE_SIZE,
                offset: 0,
            });
            setConversations(page);
            setHasMore(more);
        } catch (err) {
            toast.error(err.message || 'Failed to load conversations.');
            setConversations([]);
            setHasMore(false);
        }
    }, []);

    useEffect(() => {
        loadInbox();
    }, [loadInbox]);

    // Header Refresh must reload THIS view's conversation list, not just the parent
    // dashboard data — the inbox is component-local state that otherwise only loads
    // on mount (refresh looked dead in messages mode while the bookings-view badge,
    // fed by the parent reload, picked the new message up).
    const handleRefresh = useCallback(async () => {
        await loadInbox();
        onRefresh?.();
    }, [loadInbox, onRefresh]);

    // Append the next page on "Load more messages". Offset = current length.
    const loadMore = useCallback(async () => {
        if (isLoadingMore || !hasMore || !conversations) return;
        setIsLoadingMore(true);
        try {
            const { bookings: page, hasMore: more } = await bookingApi.getInstructorInbox({
                limit: PAGE_SIZE,
                offset: conversations.length,
            });
            setConversations(prev => [...(prev || []), ...page]);
            setHasMore(more);
        } catch (err) {
            toast.error(err.message || 'Failed to load more conversations.');
        } finally {
            setIsLoadingMore(false);
        }
    }, [isLoadingMore, hasMore, conversations]);

    // Keep the selected conversation valid as the list refreshes; default to none.
    useEffect(() => {
        if (selectedBookingId && conversations && !conversations.some(c => c.id === selectedBookingId)) {
            setSelectedBookingId(null);
        }
    }, [conversations, selectedBookingId]);

    const selectedBooking = useMemo(
        () => (conversations || []).find(c => c.id === selectedBookingId) || null,
        [conversations, selectedBookingId]
    );

    // Header badge: count of unread conversations among those LOADED. With page
    // size 30 this covers an instructor's realistic unread set; conversations
    // unread beyond the loaded pages aren't counted until "Load more" pulls them.
    const unreadMessageCount = useMemo(() => {
        if (!conversations) return 0;
        return conversations.reduce((n, b) => n + ((b.unreadCount || 0) > 0 ? 1 : 0), 0);
    }, [conversations]);

    const formatRowTime = timestamp => {
        if (!timestamp) return '';
        const dt = DateTime.fromISO(timestamp);
        if (!dt.isValid) return '';
        if (dt.hasSame(DateTime.now(), 'day')) return dt.toFormat('h:mm a');
        if (dt > DateTime.now().minus({ days: 7 })) return dt.toFormat('EEE');
        return dt.toFormat('MMM d');
    };

    const renderRow = booking => {
        const isUnread = (booking.unreadCount || 0) > 0;
        const isSelected = booking.id === selectedBookingId;
        const preview = booking.lastMessage?.message || 'No messages yet';
        const statusLabel = STATUS_LABELS[booking.status];
        const travelPeriod = travelPeriodFor(booking);
        const travelLabel = travelPeriod
            ? (travelPeriod.location?.description || travelPeriod.destination || 'Travel')
            : null;
        return (
            <button
                key={booking.id}
                type="button"
                className={`messages-inbox-row${isUnread ? ' is-unread' : ''}${isSelected ? ' is-selected' : ''}`}
                onClick={() => setSelectedBookingId(booking.id)}
            >
                <img
                    className="messages-inbox-avatar"
                    src={booking.studentInfo?.profileImageUrl || avatarPlaceholder}
                    alt={booking.studentName || 'Student avatar'}
                    onError={e => { e.target.onerror = null; e.target.src = avatarPlaceholder; }}
                />
                <div className="messages-inbox-row-main">
                    <div className="messages-inbox-row-top">
                        <span className="messages-inbox-name">{booking.studentName || 'Unknown Student'}</span>
                        {travelLabel && (
                            <span
                                className="messages-inbox-travel-pill"
                                style={{ '--period-color': travelPeriod.color || '#d946ef' }}
                                title={`Travel period: ${travelLabel}`}
                            >
                                <Icon path={mdiAirplaneTakeoff} size={0.55} />
                                {travelLabel}
                            </span>
                        )}
                        <span className="messages-inbox-time">
                            {formatRowTime(booking.lastMessage?.sentAt)}
                        </span>
                    </div>
                    <div className="messages-inbox-preview">{preview}</div>
                    <div className="messages-inbox-context">
                        <span className="messages-inbox-booking-label">Booking:</span>
                        {' '}
                        {DateTime.fromISO(booking.bookingDate).toFormat('EEE, MMM d')}
                        {booking.timeSlot?.startTime ? ` · ${booking.timeSlot.startTime}` : ''}
                        {statusLabel && (
                            <span className={`messages-inbox-status-pill ${booking.status}`}>{statusLabel}</span>
                        )}
                    </div>
                </div>
                {isUnread && (
                    <span
                        className="messages-inbox-unread-count"
                        aria-label={`${booking.unreadCount} unread`}
                    >
                        {booking.unreadCount}
                    </span>
                )}
            </button>
        );
    };

    return (
        <div className="messages-view">
            <ManageBookingsHeader
                viewMode="messages"
                onBackToBookings={onBackToBookings}
                onRefresh={handleRefresh}
                unreadMessageCount={unreadMessageCount}
            />

            <div className={`messages-view-body${selectedBooking ? ' has-selection' : ''}`}>
                {conversations === null ? (
                    <div style={{ position: 'relative', minHeight: '200px' }}>
                        <WebActivityIndicatorComponent isLoading={true} />
                    </div>
                ) : conversations.length === 0 ? (
                    <div className="no-messages">
                        <p>No message activity</p>
                        <span className="no-messages-subtitle">
                            Bookings with messages will appear here
                        </span>
                    </div>
                ) : (
                    <div className="messages-two-pane">
                        {/* LEFT — conversation list */}
                        <div className="messages-inbox-list">
                            <div className="messages-inbox-list-header">
                                <h3>Conversations</h3>
                                {/* No count: the list is paginated (scope=inbox), so a number
                                    here would only reflect the loaded page(s), not the true total. */}
                            </div>
                            <div className="messages-inbox-rows">
                                {conversations.map(renderRow)}
                                {hasMore && (
                                    <button
                                        type="button"
                                        className="messages-inbox-load-more"
                                        onClick={loadMore}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore ? 'Loading...' : 'Load more messages'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* RIGHT — reading pane (full-screen overlay on narrow via CSS) */}
                        <div className="messages-reading-pane">
                            {selectedBooking ? (
                                <MessageThread
                                    booking={selectedBooking}
                                    currentUserId={currentUserId}
                                    onMessageSent={onMessageSent}
                                    onMessagesRead={bookingId => {
                                        // Clear the unread badge locally so the inbox row + header
                                        // count update immediately, then bubble to the dashboard.
                                        setConversations(prev =>
                                            (prev || []).map(c =>
                                                c.id === bookingId ? { ...c, unreadCount: 0 } : c
                                            )
                                        );
                                        if (onMessagesRead) onMessagesRead(bookingId);
                                    }}
                                    onViewBookingDetails={onViewBookingDetails}
                                    onBack={() => setSelectedBookingId(null)}
                                    showBackButton={true}
                                />
                            ) : (
                                <div className="messages-reading-empty">
                                    <p>Select a conversation</p>
                                    <span>Choose a conversation on the left to read and reply</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessagesView;
